import { useState, useEffect, useRef } from 'react';
import { propertiesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export const PropertyFilter = ({ selectedIds, onSelectionChange }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await propertiesApi.getAll('active');
      setProperties(response.data);
      // Initialize with all properties selected if none selected
      if (selectedIds.length === 0) {
        onSelectionChange(response.data.map(p => p.id));
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProperty = (propertyId) => {
    if (selectedIds.includes(propertyId)) {
      onSelectionChange(selectedIds.filter(id => id !== propertyId));
    } else {
      onSelectionChange([...selectedIds, propertyId]);
    }
  };

  const selectAll = () => {
    onSelectionChange(properties.map(p => p.id));
  };

  const deselectAll = () => {
    onSelectionChange([]);
  };

  const allSelected = properties.length > 0 && selectedIds.length === properties.length;
  const noneSelected = selectedIds.length === 0;

  // Generate label
  const getLabel = () => {
    if (loading) return 'Loading...';
    if (noneSelected) return 'No Properties';
    if (allSelected) return 'All Properties';
    if (selectedIds.length <= 2) {
      const selectedProps = properties.filter(p => selectedIds.includes(p.id));
      return selectedProps.map(p => {
        // Shorten address to first part
        const parts = p.address.split(',');
        return parts[0].trim();
      }).join(', ');
    }
    return `${selectedIds.length} of ${properties.length} Properties`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="min-w-[180px] justify-between"
        data-testid="property-filter-trigger"
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="truncate max-w-[140px]">{getLabel()}</span>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-slate-400 transition-transform",
          isOpen && "rotate-180"
        )} />
      </Button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg py-2">
          {/* Select All / Deselect All */}
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAll}
                disabled={allSelected}
                className="flex-1 h-8 text-xs"
                data-testid="select-all-properties"
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={deselectAll}
                disabled={noneSelected}
                className="flex-1 h-8 text-xs"
                data-testid="deselect-all-properties"
              >
                Deselect All
              </Button>
            </div>
          </div>

          {/* Property List */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                Loading properties...
              </div>
            ) : properties.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                No active properties
              </div>
            ) : (
              properties.map(property => {
                const isSelected = selectedIds.includes(property.id);
                // Shorten address for display
                const shortAddress = property.address.split(',')[0].trim();
                
                return (
                  <div
                    key={property.id}
                    className={cn(
                      "px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors",
                      isSelected && "bg-slate-50"
                    )}
                    onClick={() => toggleProperty(property.id)}
                    data-testid={`property-filter-item-${property.id}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleProperty(property.id)}
                      className="pointer-events-none"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {shortAddress}
                      </p>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with count */}
          {!loading && properties.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 text-xs text-slate-500 text-center">
              {selectedIds.length} of {properties.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyFilter;
